import { useCallback, useEffect, useMemo } from "react";
import { HomeMethods } from "../../common/hooks";
import { EndpointResponse, ErrorCause, FormSubmission, FormSubmissionResponse, KitOrderValidationType, KitType, SelectOption, ShipmentType } from "../../common/types";
import Constants from "../../common/constants";
import { formatPostalCodeInput, getKitTokensJson, getMaxKitsCanUserOrder, getNumKitOrdersRemaining, getNumKitsRemaining, stringTemplate } from "../../common/Helpers";
import { getUserConfig } from "../../api/users";
import { postSubmissionV2 } from "../../api/submissions";
import Validator from "../../common/validation";
import { AppLabels } from "../../common/localization";

type HomeV2Methods = {
    hivKitTypeOptions: SelectOption[];
    selectedHivKitCode: string;
    amountNeededOptions: SelectOption[];
    handleSuccessMessageClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    handleInputChange: (event: any) => void;
    validateFormSubmission: (newData?: FormSubmission) => boolean;
    handleSubmit: (event:any) => void;
}

type HomeV2Props = {
    homeMethods: HomeMethods, 
    userId: string | undefined, 
    lang: string, 
    isValidUser: boolean, 
    isPostMessage: boolean, 
    postMessageReferrer: string
    doubleCheckWarningRef: React.MutableRefObject<HTMLDivElement | null>, 
    topOfFormRef: React.MutableRefObject<HTMLDivElement | null>
    defaultKitType?: string
}

const useHomeV2Methods = ({
    homeMethods, userId, lang, isValidUser, isPostMessage, postMessageReferrer,
    doubleCheckWarningRef, topOfFormRef, defaultKitType
}: HomeV2Props): HomeV2Methods => {
  const {
    userConfig,
    setUserConfig,
    data,
    setData,
    setServerError,
    setReady,
    setConfirmAddress,
    setIsSaving,
    setHasSubmitted,
    validated,
    setValidated,
    setValid,
    isSaving,
    confirmAddress,
    setGeneratedTokensJson
  } = homeMethods;
  
  let hivKitTypeOptions = useMemo(() => userConfig?.kitsAvailable.filter(x => x.isHivKit).map(x => ({ label: x.name, value: x.key} as SelectOption)) ?? [], [userConfig]);
  let calcDefaultKitType = useCallback((kitsAvailable?: KitType[]) => {
    const hivKits = kitsAvailable?.filter(x => x.isHivKit) ?? [];
    return defaultKitType !== undefined && hivKits.some(x => x.key === defaultKitType) 
      ? defaultKitType 
      : defaultKitType !== undefined && hivKits.some(x => x.key === Constants.HivKitCode && defaultKitType === Constants.InstiKitCode) // TODO: native app sends through insti while the key for Insti kits needs to stay as tokens. Remove this when the native app is patched.
        ? Constants.HivKitCode
        : hivKits.length > 0 
          ? hivKits[0].key 
          : Constants.HivKitCode
  }, [defaultKitType]);
  let selectedHivKitCode = useMemo(() => data.Kits.find(y => hivKitTypeOptions.some(z => z.value === y.KitTypeCode))?.KitTypeCode ?? calcDefaultKitType(userConfig?.kitsAvailable), [data, hivKitTypeOptions, calcDefaultKitType, userConfig?.kitsAvailable]);
  let amountNeededOptions = useMemo(() => {
    if(userConfig === undefined) {
      return []
    }

    let hivKitsPerOrder = userConfig.kitsAvailable.find(x => x.key === selectedHivKitCode)?.maxKitsCanUserOrder ?? 0
    let hivOrdersRemaining = getNumKitOrdersRemaining(userConfig.kitsAvailable, userConfig.kitsOrdered, selectedHivKitCode);

    let userAmountNeededOptions: SelectOption[] = [];

    if(hivOrdersRemaining <= 0) {
      userAmountNeededOptions.push({ label: "0", value: 0 });
    }

    if (hivOrdersRemaining > 0) {
      for (var i = 1; i <= hivKitsPerOrder; i++) {
        userAmountNeededOptions.push({ label: i.toFixed(0), value: i });
      }
    }
    
    return userAmountNeededOptions
  }, [ userConfig, selectedHivKitCode ]);


  useEffect(() => {
    setData((prevData) => ({
        ...prevData,
        UserID: userId,
        IsValidUser: isValidUser
      })
    );
  }, [userId, isValidUser, setData]);

  useEffect(() => {
    if (
      data.UserID === undefined || userConfig !== undefined
    ) {
      return 
    }

    (async () => {
      try {
        let localUserConfig = await getUserConfig(data.UserID!);

        const defaultKitType = calcDefaultKitType(localUserConfig.kitsAvailable);

        localUserConfig.kitsAvailable.forEach((x) => {
          if(x.isHivKit && x.key !== defaultKitType) {
            return; // continue if an HIV kit has already been included
          }
          
          data.Kits.push({
            KitTypeCode: x.key,
            NumNeeded: Math.min(
              1,
              getNumKitsRemaining(localUserConfig.kitsOrdered, x.key),
            )
          });
        });

        setUserConfig(localUserConfig);
        setData(data);
      } catch (err: any) {
        setServerError(err)
      } finally {
        setReady(true);
      }
    })();
  }, [data, data.UserID, setData, setReady, setServerError, setUserConfig, userConfig, calcDefaultKitType]);

  const getSuccessMessage = (event: React.MouseEvent<HTMLDivElement>) => {
    switch((event.target as HTMLElement).id) {
      case 'get-prep': return 'GetPrEP'
      case 'get-treatment': return 'GetTreatment'
      default: return 'Proceed'
    }
  }

  const handleSuccessMessageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === null || (event.target as HTMLElement).tagName !== 'BUTTON') return;

    event.preventDefault(); // Prevent default browser navigation
    
    if (isPostMessage && postMessageReferrer !== "") {
      window.parent.postMessage(
        { type: getSuccessMessage(event) },
        atob(postMessageReferrer),
      );
    } else {
      (window as any).ReactNativeWebView?.postMessage(JSON.stringify({ type: getSuccessMessage(event) }));
    }
  };

  const handleInputChange = (event: any) => {
    const target = event.target;

    var value = target.value;

    if (target.name === "PostalCode") {
      value = formatPostalCodeInput(value);
    }

    if (target.name === "ShipmentType") {
      setConfirmAddress(false);
    }

    if(validated) {
      validateFormSubmission();
    }

    setData((data) => ({ ...data, [target.name]: value }));
  };

  const trimInputs = useCallback((data: FormSubmission) => {
    data.FirstName = data.FirstName.trim();
    data.LastName = data.LastName.trim();
    data.StreetName = data.StreetName.trim();
    data.BuildingNumber = data.BuildingNumber.trim();
    data.Unit = data.Unit.trim();
    data.City = data.City.trim();
    data.PostalCode = data.PostalCode.trim();

    setData((data) => ({ ...data }));
  }, [setData]);

  const clearDeliveryInputs = useCallback((data: FormSubmission) => {
    data.FirstName = "";
    data.LastName = "";
    data.StreetName = "";
    data.BuildingNumber = "";
    data.Unit = "";
    data.City = "";
    data.PostalCode = "";
    data.Province = "";

    setData((data) => ({ ...data }));
  }, [setData]);

  const clearPickupInputs = useCallback((data: FormSubmission) => {
    data.LocationID = 0;

    setData((data) => ({ ...data }));
  }, [setData]);

  const validateFormSubmission = useCallback((newData?: FormSubmission): boolean => {
    setValidated(true);
    const isValid = Validator.validateFormSubmission(
      newData || data,
      userConfig!,
      true,
      true,
      KitOrderValidationType.NumOrders
    );

    setValid(isValid);

    return isValid;
  }, [data, setValidated, userConfig, setValid]);

  const handleSubmit = useCallback((event: any) => {
    event.preventDefault();
    let { kitsAvailable } = userConfig!;

    // Trim all inputs
    trimInputs(data);

    if(isSaving) {
      return;
    } else if (!validateFormSubmission()) {
      if(!confirmAddress) {
        topOfFormRef.current!.scrollIntoView({ behavior: 'smooth' });
      }

      return;
    }

    // If submitting a pickup form make sure the customer address fields are wiped
    if (data.ShipmentType === ShipmentType.Pickup) {
      clearDeliveryInputs(data);
    } else {
      clearPickupInputs(data);
    }

    if (data.ShipmentType === ShipmentType.Delivery && !confirmAddress) {
      setConfirmAddress(true);
      doubleCheckWarningRef.current!.scrollIntoView();
      return;
    }

    //check if kits contains both HIV and COVID kits arrays for plazus' side of things
    let hivKit = data.Kits.find((x) => x.KitTypeCode === selectedHivKitCode);
    let covidKit = data.Kits.find(
      (x) => x.KitTypeCode === Constants.CovidKitCode,
    );

    //if HIV or covid kit is not in the array, add it with 0 quantity
    if (hivKit === undefined) {
      data.Kits.push({ KitTypeCode: selectedHivKitCode, NumNeeded: 0 });
    }
    if (covidKit === undefined) {
      data.Kits.push({ KitTypeCode: Constants.CovidKitCode, NumNeeded: 0 });
    }

    setIsSaving(true);

    postSubmissionV2(lang, data)
      .then((resp: any) => {
        let responseData: EndpointResponse = resp.data;
        let serverError = "";

        if (responseData.status) {
          setIsSaving(false);
          setHasSubmitted(true);
          setGeneratedTokensJson(getKitTokensJson(
              responseData.data as FormSubmissionResponse,
              userConfig?.kitsAvailable ?? []
            ))
        } else {
          if (responseData.reason === ErrorCause.TooManyKitsRequested) {
            if (
              responseData.data !== null &&
              "numKitsAlreadyOrdered" in responseData.data
            ) {
              let numKitsOrdered = Number(
                responseData.data.numKitsAlreadyOrdered,
              );

              if (numKitsOrdered === 3) {
                serverError = stringTemplate(
                  AppLabels.CantSubmitErrorReceivedAll,
                  {
                    "1": getMaxKitsCanUserOrder(
                      kitsAvailable,
                      selectedHivKitCode,
                    ),
                  },
                );
              } else {
                serverError = stringTemplate(
                  AppLabels.CantSubmitErrorReceivedSome,
                  {
                    "1": numKitsOrdered,
                    "2":
                      getMaxKitsCanUserOrder(
                        kitsAvailable,
                        selectedHivKitCode,
                      ) - numKitsOrdered,
                    "3": getMaxKitsCanUserOrder(
                      kitsAvailable,
                      selectedHivKitCode,
                    ),
                  },
                );
              }
            } else {
              serverError = AppLabels.CantSubmitErrorInternal;
              console.error(
                "Error - too many kits ordered but no amount returned",
              );
            }
          } else if (responseData.reason === ErrorCause.SubmissionDisabled) {
            serverError = AppLabels.NotLiveNotice;
          }

          setIsSaving(false);
          setHasSubmitted(false);
          setServerError(serverError);
        }
      })
      .catch((err) => {
        let message =
          err !== undefined &&
          err.response !== undefined &&
          err.response.data !== undefined &&
          err.response.data.length > 0
            ? err.response.data
            : AppLabels.CantSubmitErrorInternal;

        setIsSaving(false);
        setHasSubmitted(false);
        setServerError(message);
      });
  }, [clearDeliveryInputs, clearPickupInputs, confirmAddress, data, doubleCheckWarningRef, isSaving, lang, setConfirmAddress, setGeneratedTokensJson, setHasSubmitted, setIsSaving, setServerError, topOfFormRef, trimInputs, userConfig, validateFormSubmission, selectedHivKitCode]);;


  return {
    hivKitTypeOptions,
    selectedHivKitCode,
    amountNeededOptions,
    handleSuccessMessageClick,
    handleInputChange,
    validateFormSubmission,
    handleSubmit
  }
}

export default useHomeV2Methods;